import React, { useEffect, useRef, useCallback } from 'react';

const SimpleMediaPipe = ({ onGesture, onError }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const handsRef = useRef(null);
  const cameraRef = useRef(null);
  const lastGestureTime = useRef(0);

  // Simple gesture detection logic
  const detectGesture = useCallback((landmarks) => {
    if (!landmarks || landmarks.length === 0) return null;

    const hand = landmarks[0];
    const fingerTips = [4, 8, 12, 16, 20]; // Thumb, Index, Middle, Ring, Pinky tips
    const fingerBases = [3, 6, 10, 14, 18]; // Finger base joints

    // Check if fingers are extended
    const isFingerExtended = (tipIndex, baseIndex) => {
      return hand[tipIndex].y < hand[baseIndex].y;
    };

    const extendedFingers = fingerTips.map((tip, i) => 
      isFingerExtended(tip, fingerBases[i])
    );

    const extendedCount = extendedFingers.filter(Boolean).length;

    // Gesture detection
    let gesture = null;

    // Peace sign - only index and middle fingers extended
    if (extendedCount === 2 && extendedFingers[1] && extendedFingers[2] && 
        !extendedFingers[0] && !extendedFingers[3] && !extendedFingers[4]) {
      gesture = 'peace';
    }
    // Pointing (only index finger extended)
    else if (extendedCount === 1 && extendedFingers[1]) {
      gesture = 'point';
    }
    // Open hand - check for swipe
    else if (extendedCount >= 3) {
      const wristX = hand[0].x;
      
      // Simple position-based detection
      if (wristX < 0.3) {
        gesture = 'swipe_right'; // Hand on left = next
      } else if (wristX > 0.7) {
        gesture = 'swipe_left'; // Hand on right = previous
      }
    }

    return gesture;
  }, []);

  // Process hand detection results
  const onResults = useCallback((results) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
      // Draw hand landmarks
      for (const landmarks of results.multiHandLandmarks) {
        // Draw landmarks
        ctx.fillStyle = 'rgba(100, 181, 246, 0.8)';
        landmarks.forEach((landmark) => {
          ctx.beginPath();
          ctx.arc(
            landmark.x * canvas.width,
            landmark.y * canvas.height,
            4,
            0,
            2 * Math.PI
          );
          ctx.fill();
        });
      }

      // Detect gesture
      const gesture = detectGesture(results.multiHandLandmarks);
      
      if (gesture) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.font = '12px Arial';
        ctx.fillText(`Gesture: ${gesture}`, 10, 20);
      }
      
      // Throttle gesture detection
      const now = Date.now();
      if (gesture && now - lastGestureTime.current > 800) {
        lastGestureTime.current = now;
        onGesture?.(gesture);
      }
    }
  }, [detectGesture, onGesture]);

  // Initialize MediaPipe with basic setup
  useEffect(() => {
    let isComponentMounted = true;
    let hands = null;
    let camera = null;

    const initializeMediaPipe = async () => {
      try {
        // Ensure we're still mounted
        if (!isComponentMounted) return;

        // Clean up any existing instances first
        if (handsRef.current) {
          try {
            handsRef.current.close();
          } catch (e) {
            console.warn('Error closing existing hands instance:', e);
          }
          handsRef.current = null;
        }

        if (cameraRef.current) {
          try {
            cameraRef.current.stop();
          } catch (e) {
            console.warn('Error stopping existing camera:', e);
          }
          cameraRef.current = null;
        }

        // Load MediaPipe scripts dynamically
        if (!window.Hands) {
          console.log('Loading MediaPipe scripts...');
          
          // Load main MediaPipe script
          await new Promise((resolve, reject) => {
            if (!isComponentMounted) return reject(new Error('Component unmounted'));
            
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/hands@0.4.1675469240/hands.js';
            script.onload = resolve;
            script.onerror = () => reject(new Error('Failed to load hands.js'));
            document.head.appendChild(script);
          });

          // Load camera utils
          await new Promise((resolve, reject) => {
            if (!isComponentMounted) return reject(new Error('Component unmounted'));
            
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils@0.3.1640029074/camera_utils.js';
            script.onload = resolve;
            script.onerror = () => reject(new Error('Failed to load camera_utils.js'));
            document.head.appendChild(script);
          });
        }

        // Ensure we're still mounted after async operations
        if (!isComponentMounted) return;

        // Initialize with the global Hands constructor
        hands = new window.Hands({
          locateFile: (file) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/hands@0.4.1675469240/${file}`;
          }
        });

        hands.setOptions({
          maxNumHands: 1,
          modelComplexity: 0,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.3,
          staticImageMode: false
        });

        hands.onResults(onResults);
        handsRef.current = hands;

        // Initialize camera only if component is still mounted
        if (videoRef.current && isComponentMounted) {
          camera = new window.Camera(videoRef.current, {
            onFrame: async () => {
              try {
                // Double check that hands instance exists and component is mounted
                if (handsRef.current && videoRef.current && isComponentMounted) {
                  await handsRef.current.send({ image: videoRef.current });
                }
              } catch (frameError) {
                if (frameError.message && frameError.message.includes('deleted object')) {
                  console.warn('MediaPipe object was deleted, reinitializing...');
                  // Trigger reinitialization on next cycle
                  setTimeout(() => {
                    if (isComponentMounted && onError) {
                      onError();
                    }
                  }, 100);
                } else {
                  console.warn('Frame processing error:', frameError);
                }
              }
            },
            width: 320,
            height: 240
          });

          cameraRef.current = camera;
          await camera.start();
          console.log('Simple MediaPipe initialized successfully');
        }
      } catch (error) {
        console.error('Simple MediaPipe initialization failed:', error);
        if (isComponentMounted && onError) {
          setTimeout(() => onError(), 1000);
        }
      }
    };

    // Delay initialization to ensure DOM is ready
    const initTimeout = setTimeout(initializeMediaPipe, 1500);

    // Cleanup function
    return () => {
      isComponentMounted = false;
      clearTimeout(initTimeout);
      
      // Clean up camera first
      if (camera) {
        try {
          camera.stop();
        } catch (e) {
          console.warn('Error stopping camera during cleanup:', e);
        }
      }
      
      // Then clean up hands
      if (hands) {
        try {
          hands.close();
        } catch (e) {
          console.warn('Error closing hands during cleanup:', e);
        }
      }
      
      // Clear refs
      handsRef.current = null;
      cameraRef.current = null;
    };
  }, [onResults, onError]);

  return (
    <div className="simple-mediapipe">
      <video
        ref={videoRef}
        style={{ display: 'none' }}
        autoPlay
        playsInline
        muted
      />
      <canvas
        ref={canvasRef}
        width={200}
        height={150}
        style={{
          position: 'fixed',
          top: '20px',
          right: '220px',
          border: '2px solid rgba(76, 175, 80, 0.3)',
          borderRadius: '12px',
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000
        }}
      />
    </div>
  );
};

export default SimpleMediaPipe;